import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../../common/breadcrumbs";
import { FAQTYPES, SWAL_SETTINGS } from "../../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../../utils/commonfunction";
import * as packageManagement from "../../../services/packageManagement";
import * as Yup from "yup";
import CustomError from "../../../../../utils/customError";
import { useTranslation } from "react-i18next";

const AddPackages = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    {
      title: 'Packages',
      url: "/admin/packageManagement/packages/list/1",
    },
    { title: t("link_add"), url: "" },
  ];

  // 🧩 Validation Schema
  const validationSchema = Yup.object().shape({
    Name: Yup.string()
    .trim()
    .required(t("label_name_error"))
    .test("no-spaces", t("label_name_error"), (value) => value && value.trim().length > 0)
    .test(
      "max-length",
      t("label_name_max_words_error", { count: 50 }), // "Name cannot exceed 50 characters"
      (value) => !value || value.trim().length <= 50
    ),
  
  Description: Yup.string()
    .trim()
    .required(t("label_description_error"))
    .test(
      "max-length",
      t("label_desc_max_words_error", { count: 150 }), // "Description cannot exceed 150 characters"
      (value) => !value || value.trim().length <= 150
    ),
    Price: Yup.number()
      .typeError(t("label_price_Number_error"))
      .required(t("label_price_error"))
      .min(1, t("label_price_min_error"))
      .max(1000000, t("label_price_max_error")),
    SavedPrice: Yup.number()
      .typeError(t("label_saved_price_Number_error") || "Please enter valid discount")
      .required(t("label_saved_price_error") || "Discount is required")
      .min(0, "Discount cannot be negative")
      .max(1000000, t("label_price_max_error")),
    hsn: Yup.string().trim().max(30, "HSN code cannot exceed 30 characters"),
    Hours: Yup.string().when("Type", {
      is: "Hours",
      then: (schema) => schema.required(t("label_hours_error")).test(
        "is-valid-hours",
        t("label_hours_Number_error"),
        (value) => {
          if (!value) return false;
          const regex = /^\d+h?$/;
          return regex.test(value);
        }
      ).test(
        "min-hours",
        t("label_hours_min_error"),
        (value) => {
          if (!value) return false;
          const num = parseInt(value.replace("h", ""), 10);
          return num >= 1;
        }
      ),
      otherwise: (schema) => schema.notRequired(),
    }),
    AiClicks: Yup.number().when("Type", {
      is: "AiClicks",
      then: (schema) => schema.required("AI Clicks are required").min(1, "Minimum 1 click required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    Type: Yup.string().required("Package type is required"),
  });



 // 🧩 Formik Setup
 const formik = useFormik({
  enableReinitialize: true,
  initialValues: {
    Name: "",
    Description: "",
    Price : 0 , 
    SavedPrice : 0,
    hsn: "",
    Hours : null,
    AiClicks: 0,
    Type: "Hours"


  },
  validationSchema,
  validateOnBlur: false,
  onSubmit: (values, { setSubmitting, resetForm }) => {
    globalLoader(true);
    setSubmitted(true);

    const formData = new FormData();
    formData.append("Name", values.Name);
    formData.append("Description", values.Description);
    formData.append("Price", values.Price);
    formData.append("SavedPrice", values.SavedPrice);
    formData.append("hsn", values.hsn || "");
    formData.append("Hours", values.Hours || "");
    formData.append("AiClicks", values.AiClicks || 0);
    formData.append("Type", values.Type);

    packageManagement
      .Add(formData)
      .then((response) => {
        setSubmitting(false);
        if (response.success) {
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS,
          });
          globalLoader(false);
          resetForm();
          navigate("/admin/packageManagement/packages/list/1", { replace: true });
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
          setSubmitted(false);
          globalLoader(false);
        }
      })
      .catch((error) => console.log("error ====> ", error));
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">
                    {t("link_add")} Package
                  </h6>
                </div>
                <div className="row row-sm">

                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="Name" className="text-left d-flex"
                      title="Please enter name">
                      {t("list_heading_package_name")}
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="Name"
                      id="Name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Name}
                      className="form-control"
                      placeholder="Enter package name"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Name" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="Description" className="text-left d-flex"  
                    title="Please enter description">
                      {t("list_heading_desc")}
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="Description"
                      id="Description"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Description}
                      className="form-control"
                      placeholder="Enter description"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Description" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="Price" className="text-left d-flex"
                     title="Please enter price in number format only">
                      {t("list_heading_price")}
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="Price"
                      id="Price"
                      type="Number"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Price}
                      className="form-control"
                      placeholder="Price"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Price" form={formik} />
                      {console.log(formik.errors)}
                    </span>
                  </div>

                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="SavedPrice" className="text-left d-flex"
                    title="Please enter discount in number format only">
                      Discount
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input
                      name="SavedPrice"
                      id="SavedPrice"
                      type="Number"
                      onChange={formik.handleChange}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        let val = e.target.value;
                        if (val && !val.toString().includes(".")) {
                          val = `${val}.00`;
                          formik.setFieldValue("SavedPrice", val);
                        }
                      }}
                      value={formik.values.SavedPrice}
                      className="form-control"
                      title="Please enter discount in number format only"
                      placeholder="Discount"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="SavedPrice" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="hsn" className="text-left d-flex" title="Please enter HSN code">
                      HSN Code
                    </label>
                    <input
                      name="hsn"
                      id="hsn"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.hsn}
                      className="form-control"
                      placeholder="e.g. 998313"
                    />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="hsn" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-6 text-center form-group">
                    <label htmlFor="Type" className="text-left d-flex">
                      Package Type
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <select
                      name="Type"
                      id="Type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Type}
                      className="form-control"
                    >
                      <option value="Hours">Streaming Hours</option>
                      <option value="AiClicks">AI Scan Clicks</option>
                    </select>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="Type" form={formik} />
                    </span>
                  </div>

                  {formik.values.Type === "Hours" ? (
                    <div className="col-md-6 text-center form-group">
                      <label
                        htmlFor="Hours"
                        className="text-left d-flex"
                        title="Please enter hours in number format only">
                        {t("list_heading_hours")}
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="Hours"
                        id="Hours"
                        type="text"
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, "");
                          formik.setFieldValue("Hours", val);
                        }}
                        onBlur={(e) => {
                          formik.handleBlur(e);
                          let val = e.target.value.trim();
                          if (val && !val.endsWith("h")) {
                            formik.setFieldValue("Hours", `${val}h`);
                          }
                        }}
                        value={formik.values.Hours}
                        className="form-control"
                        placeholder="Enter hours"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="Hours" form={formik} />
                      </span>
                    </div>
                  ) : (
                    <div className="col-md-6 text-center form-group">
                      <label
                        htmlFor="AiClicks"
                        className="text-left d-flex"
                        title="Please enter AI clicks">
                        AI Clicks
                        <span className="requirestar">*</span>{" "}
                      </label>
                      <input
                        name="AiClicks"
                        id="AiClicks"
                        type="Number"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.AiClicks}
                        className="form-control"
                        placeholder="Enter AI clicks"
                      />
                      <span className="text-danger d-flex text-left">
                        <CustomError name="AiClicks" form={formik} />
                      </span>
                    </div>
                  )}


                  <div className="mt-5">
                    <button
                      className="btn btn-info mr-2"
                      type="submit"
                      disabled={submitted ? true : null}
                      // onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button
                      className="btn ripple btn-secondary"
                      type="button"
                      disabled={submitted ? true : null}
                      onClick={() => navigate(-1)}
                    >
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default AddPackages;
